import type { ConsumerValueSchema } from "@cogo/schema/action-consumer-catalog"

export type { ConsumerValueSchema } from "@cogo/schema/action-consumer-catalog"

export type ContentHash = `sha256:${string}`

export type HostCapabilityId = `host.${string}`

export type RuntimeActionHandler = (
  params: Record<string, unknown>,
  context?: Record<string, unknown>,
) => unknown | Promise<unknown>

export interface ActionConsumerReadObservation {
  executionId: string
  stepPath: string
  requestedAction: string
  canonicalAction: string
  parameterPath: string
  operation: "get" | "has" | "ownKeys"
  valueHash?: ContentHash
}

export type ActionConsumerObserver = (
  observation: ActionConsumerReadObservation,
) => void

export interface ActionConsumerProducerObservation {
  executionId: string
  stepPath: string
  requestedAction: string
  canonicalAction: string
  parameterPath: string
  present: boolean
  valueHash?: ContentHash
}

export type ActionConsumerProducerObserver = (
  observation: ActionConsumerProducerObservation,
) => void

export interface ActionConsumerObservationScope {
  executionId: string
  observer: ActionConsumerObserver
}

export type ActionConsumerObservationScopeFactory = (
  executionId: string,
  scopeBindingHash: ContentHash,
  observer: ActionConsumerObserver,
  outcomeObserver?: ActionConsumerOutcomeObserver,
  producerObserver?: ActionConsumerProducerObserver,
) => ActionConsumerObservationScope

export type ActionConsumerOutcomeDescriptor =
  | {
      kind: "state-target"
      condition?: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean }
      targetParam?: string
      defaultTarget?: string
      defaultTargetFromParam?: { prefix: string; param: string; fallback?: string }
      targetSuffixes?: readonly string[]
      envelope?: ConsumerValueSchema
    }
  | {
      kind: "return-capture"
      condition?: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean }
      capture: "step-name"
      envelope?: ConsumerValueSchema
    }
  | {
      kind: "host-effect"
      condition?: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean }
      capabilityId: HostCapabilityId
      operation: string
    }
  | {
      kind: "host-registry"
      condition?: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean }
      capabilityId: HostCapabilityId
      selectorParam: string
      payloadParam?: string
    }

export interface ActionConsumerOutcomeObservation {
  executionId: string
  stepPath: string
  requestedAction: string
  canonicalAction: string
  descriptor: ActionConsumerOutcomeDescriptor
  selectorValueHash?: ContentHash
  valueHash?: ContentHash
}

export type ActionConsumerOutcomeObserver = (
  observation: ActionConsumerOutcomeObservation,
) => void

export type ActionConsumerAtomicOutcome =
  | {
      kind: "state-target"
      targetParam?: string
      defaultTarget?: string
      defaultTargetFromParam?: {
        prefix: string
        param: string
        fallback?: string
      }
      targetSuffixes?: readonly string[]
      envelope?: ConsumerValueSchema
    }
  | {
      kind: "return-capture"
      capture: "step-name"
      envelope?: ConsumerValueSchema
    }
  | { kind: "host-effect"; capability: HostCapabilityId; operation: string }
  | {
      kind: "host-registry"
      capability: HostCapabilityId
      selectorParam: string
      payloadParam?: string
    }

export type ActionConsumerOutcome =
  | ActionConsumerAtomicOutcome
  | {
      kind: "effect-set"
      branches: readonly {
        when?: {
          param: string
          equals?: readonly (string | number | boolean | null)[]
          present?: boolean
        }
        effects: readonly ActionConsumerAtomicOutcome[]
      }[]
    }
  | { kind: "none" }

export interface ActionConsumerContract {
  schemaVersion: "cogo.action-consumer.v1"
  action: string
  runtimeAliases: readonly string[]
  reads: readonly {
    path: string
    aliases?: readonly string[]
    required: boolean
    role: "input" | "selector" | "payload" | "target" | "guard"
  }[]
  outcome: ActionConsumerOutcome
  requiredHostCapabilities: readonly HostCapabilityId[]
  missingConsumerPolicy: "reject-before-side-effects" | "block-host"
}

export function resolveActionConsumerStateTarget(
  outcome: Extract<ActionConsumerAtomicOutcome, { kind: "state-target" }>,
  params: Record<string, unknown>,
): string | undefined {
  const explicitTarget = outcome.targetParam
    ? normalizeStateTarget(params[outcome.targetParam])
    : undefined
  if (explicitTarget) return explicitTarget

  const literalDefault = normalizeStateTarget(outcome.defaultTarget)
  if (literalDefault) return literalDefault

  const derived = outcome.defaultTargetFromParam
  if (!derived) return undefined
  const parameterValue = params[derived.param]
  const normalizedParameter = typeof parameterValue === "string"
    ? parameterValue.trim()
    : parameterValue
  const source = normalizedParameter === undefined || normalizedParameter === null || normalizedParameter === ""
    ? derived.fallback
    : normalizedParameter
  if (source === undefined || source === "") {
    return undefined
  }
  return normalizeStateTarget(`${derived.prefix}${String(source)}`)
}

function normalizeStateTarget(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  const target = String(value).replace(/^#/, "")
  return target || undefined
}

interface ObservationScopePrivateState {
  salt: string
  objectIds: WeakMap<object, number>
  nextObjectId: number
  outcomeObserver?: ActionConsumerOutcomeObserver
  producerObserver?: ActionConsumerProducerObserver
}

const OBSERVATION_SCOPE_PRIVATE = new WeakMap<
  ActionConsumerObservationScope,
  ObservationScopePrivateState
>()

export function createActionConsumerObservationScope(
  executionId: string,
  observer: ActionConsumerObserver,
  outcomeObserver?: ActionConsumerOutcomeObserver,
  producerObserver?: ActionConsumerProducerObserver,
): ActionConsumerObservationScope {
  return createObservationScopeWithSalt(
    executionId,
    observer,
    randomObservationSalt(),
    outcomeObserver,
    producerObserver,
  )
}

export function createActionConsumerObservationScopeFactory(
  privateKey: Uint8Array,
): ActionConsumerObservationScopeFactory {
  if (
    !(privateKey instanceof Uint8Array)
    || Object.getPrototypeOf(privateKey) !== Uint8Array.prototype
    || Reflect.ownKeys(privateKey).some((key) => typeof key === "symbol" || !/^\d+$/.test(key))
    || privateKey.byteLength < 32
  ) throw new Error("Action consumer observation scope factory requires a private 32-byte key")
  const secret = Uint8Array.from(privateKey)
  return (
    executionId,
    scopeBindingHash,
    observer,
    outcomeObserver,
    producerObserver,
  ) => {
    if (!/^sha256:[a-f0-9]{64}$/.test(scopeBindingHash)) throw new Error("Action consumer observation scope factory requires a content hash binding")
    const salt = hmacSha256ActionConsumerText(secret, `cogo.action-consumer.scope.v1\u0000${scopeBindingHash}\u0000${executionId}`)
    return createObservationScopeWithSalt(executionId, observer, salt, outcomeObserver, producerObserver)
  }
}

function createObservationScopeWithSalt(
  executionId: string,
  observer: ActionConsumerObserver,
  salt: string,
  outcomeObserver?: ActionConsumerOutcomeObserver,
  producerObserver?: ActionConsumerProducerObserver,
): ActionConsumerObservationScope {
  if (!executionId) throw new Error("Action consumer observation scope requires an executionId")
  if (typeof observer !== "function") throw new Error("Action consumer observation scope requires an observer")
  if (outcomeObserver !== undefined && typeof outcomeObserver !== "function") throw new Error("Action consumer observation scope outcome observer must be a function")
  if (producerObserver !== undefined && typeof producerObserver !== "function") throw new Error("Action consumer observation scope producer observer must be a function")
  const privateState: ObservationScopePrivateState = {
    salt,
    objectIds: new WeakMap<object, number>(),
    nextObjectId: 1,
    ...(outcomeObserver ? { outcomeObserver } : {}),
    ...(producerObserver ? { producerObserver } : {}),
  }
  const scope = Object.freeze({
    executionId,
    observer,
  })
  OBSERVATION_SCOPE_PRIVATE.set(scope, privateState)
  return scope
}

function conditionMatches(
  condition: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean },
  params: Record<string, unknown>,
): boolean {
  const value = params[condition.param]
  if (condition.equals) return condition.equals.some((candidate) => Object.is(candidate, value))
  const present = value !== undefined && value !== null
  return condition.present === present
}

function selectedOutcomes(
  contract: ActionConsumerContract,
  params: Record<string, unknown>,
): readonly { outcome: ActionConsumerAtomicOutcome; condition?: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean } }[] {
  if (contract.outcome.kind === "none") return []
  if (contract.outcome.kind !== "effect-set") return [{ outcome: contract.outcome }]
  const selected = contract.outcome.branches.find((branch) => branch.when && conditionMatches(branch.when, params))
    ?? contract.outcome.branches.find((branch) => !branch.when)
  if (!selected) return []
  return selected.effects.map((outcome) => ({ outcome, ...(selected.when ? { condition: selected.when } : {}) }))
}

function outcomeDescriptor(
  outcome: ActionConsumerAtomicOutcome,
  condition?: { param: string; equals?: readonly (string | number | boolean | null)[]; present?: boolean },
): ActionConsumerOutcomeDescriptor {
  const selectedCondition = condition ? { condition: { ...condition, ...(condition.equals ? { equals: [...condition.equals] } : {}) } } : {}
  if (outcome.kind === "state-target") return { ...outcome, ...selectedCondition }
  if (outcome.kind === "return-capture") return { ...outcome, ...selectedCondition }
  if (outcome.kind === "host-effect") return { kind: outcome.kind, ...selectedCondition, capabilityId: outcome.capability, operation: outcome.operation }
  return { kind: outcome.kind, ...selectedCondition, capabilityId: outcome.capability, selectorParam: outcome.selectorParam, ...(outcome.payloadParam ? { payloadParam: outcome.payloadParam } : {}) }
}

export function observeActionConsumerOutcome(
  scope: ActionConsumerObservationScope,
  metadata: { stepPath: string; requestedAction: string; canonicalAction: string },
  contract: ActionConsumerContract,
  params: Record<string, unknown>,
  result: unknown,
  readState: (path: string) => unknown,
): void {
  const privateState = OBSERVATION_SCOPE_PRIVATE.get(scope)
  if (!privateState) throw new Error("Action consumer observation scope was not created by this runtime")
  if (!privateState.outcomeObserver) return
  for (const selected of selectedOutcomes(contract, params)) {
    const descriptor = outcomeDescriptor(selected.outcome, selected.condition)
    let value: unknown = result
    let hasValue = selected.outcome.kind === "return-capture"
    let selectorValueHash: ContentHash | undefined
    if (selected.outcome.kind === "state-target") {
      const target = resolveActionConsumerStateTarget(selected.outcome, params)
      if (target) {
        value = readState(target)
        hasValue = true
      }
    } else if (selected.outcome.kind === "host-registry") {
      selectorValueHash = hashConsumerValue(params[selected.outcome.selectorParam], privateState)
      value = selected.outcome.payloadParam ? params[selected.outcome.payloadParam] : params[selected.outcome.selectorParam]
      hasValue = true
    }
    privateState.outcomeObserver({
      executionId: scope.executionId,
      stepPath: metadata.stepPath,
      requestedAction: metadata.requestedAction,
      canonicalAction: metadata.canonicalAction,
      descriptor,
      ...(selectorValueHash ? { selectorValueHash } : {}),
      ...(hasValue ? { valueHash: hashConsumerValue(value, privateState) } : {}),
    })
  }
}

export function observeActionConsumerReads(
  params: Record<string, unknown>,
  scope: ActionConsumerObservationScope,
  metadata: {
    stepPath: string
    requestedAction: string
    canonicalAction: string
  },
  contract: ActionConsumerContract,
): Record<string, unknown> {
  const proxies = new WeakMap<object, Map<string, object>>()
  const privateState = OBSERVATION_SCOPE_PRIVATE.get(scope)
  if (!privateState) {
    throw new Error("Action consumer observation scope was not created by this runtime")
  }
  if (privateState.producerObserver) {
    for (const read of contract.reads) {
      const resolved = resolveDeclaredParameter(params, read.path)
      privateState.producerObserver({
        executionId: scope.executionId,
        stepPath: metadata.stepPath,
        requestedAction: metadata.requestedAction,
        canonicalAction: metadata.canonicalAction,
        parameterPath: read.path,
        present: resolved.present,
        ...(resolved.present ? { valueHash: hashConsumerValue(resolved.value, privateState) } : {}),
      })
    }
  }

  const wrap = (value: unknown, path: string): unknown => {
    if (!isProxySafe(value)) return value
    const byPath = proxies.get(value) ?? new Map<string, object>()
    const existing = byPath.get(path)
    if (existing) return existing
    const proxy = new Proxy(value, {
      get(target, property, receiver) {
        const result = Reflect.get(target, property, receiver)
        if (typeof property === "string") {
          const parameterPath = joinParameterPath(path, property)
          scope.observer({
            executionId: scope.executionId,
            stepPath: metadata.stepPath,
            requestedAction: metadata.requestedAction,
            canonicalAction: metadata.canonicalAction,
            parameterPath,
            operation: "get",
            valueHash: hashConsumerValue(result, privateState),
          })
          return wrap(result, parameterPath)
        }
        return result
      },
      has(target, property) {
        if (typeof property === "string") {
          scope.observer({
            executionId: scope.executionId,
            stepPath: metadata.stepPath,
            requestedAction: metadata.requestedAction,
            canonicalAction: metadata.canonicalAction,
            parameterPath: joinParameterPath(path, property),
            operation: "has",
          })
        }
        return Reflect.has(target, property)
      },
      ownKeys(target) {
        scope.observer({
          executionId: scope.executionId,
          stepPath: metadata.stepPath,
          requestedAction: metadata.requestedAction,
          canonicalAction: metadata.canonicalAction,
          parameterPath: path || "$",
          operation: "ownKeys",
        })
        return Reflect.ownKeys(target)
      },
    })
    byPath.set(path, proxy)
    proxies.set(value, byPath)
    return proxy
  }

  return wrap(params, "") as Record<string, unknown>
}

function resolveDeclaredParameter(
  params: Record<string, unknown>,
  path: string,
): { present: boolean; value?: unknown } {
  let current: unknown = params
  for (const segment of path.split(".")) {
    if (!isProxySafe(current)) return { present: false }
    const descriptor = Object.getOwnPropertyDescriptor(current, segment)
    if (!descriptor || !("value" in descriptor)) return { present: false }
    current = descriptor.value
  }
  return current === undefined ? { present: false } : { present: true, value: current }
}

function joinParameterPath(parent: string, property: string): string {
  return parent ? `${parent}.${property}` : property
}

function hashConsumerValue(
  value: unknown,
  privateState: ObservationScopePrivateState,
): ContentHash {
  return `sha256:${sha256ActionConsumerText(`${privateState.salt}:${stableSerialize(value, privateState)}`)}`
}

/**
 * Correlate producer, consumer, state, and host-effect values inside one
 * execution without exposing either the raw value or the scope-private salt.
 */
export function hashActionConsumerValueInScope(
  scope: ActionConsumerObservationScope,
  value: unknown,
): ContentHash {
  const privateState = OBSERVATION_SCOPE_PRIVATE.get(scope)
  if (!privateState) {
    throw new Error("Action consumer observation scope was not created by this runtime")
  }
  return hashConsumerValue(value, privateState)
}

function stableSerialize(
  value: unknown,
  privateState: ObservationScopePrivateState,
  seen = new WeakSet<object>(),
): string {
  if (value === null) return "null"
  if (value === undefined) return "undefined"
  if (typeof value === "string") return `string:${JSON.stringify(value)}`
  if (typeof value === "number") return `number:${Number.isNaN(value) ? "NaN" : String(value)}`
  if (typeof value === "boolean") return `boolean:${value}`
  if (typeof value === "bigint") return `bigint:${value.toString()}`
  if (typeof value === "symbol") return `symbol:${String(value.description ?? "")}`
  if (typeof value === "function") return "function"
  if (!isProxySafe(value)) {
    const existingId = privateState.objectIds.get(value)
    const id = existingId ?? privateState.nextObjectId++
    if (existingId === undefined) privateState.objectIds.set(value, id)
    const brand = Object.prototype.toString.call(value)
    return `branded:${brand}:${id}`
  }
  if (seen.has(value)) return "circular"
  seen.add(value)
  if (Array.isArray(value)) {
    const descriptors = Object.getOwnPropertyDescriptors(value)
    const entries = Object.keys(descriptors)
      .filter((key) => key !== "length")
      .sort()
      .map((key) => `${JSON.stringify(key)}:${serializeDescriptor(descriptors[key], privateState, seen)}`)
    return `array:${value.length}:[${entries.join(",")}]`
  }
  const descriptors = Object.getOwnPropertyDescriptors(value)
  return `object:{${Object.keys(descriptors).sort().map((key) =>
    `${JSON.stringify(key)}:${serializeDescriptor(descriptors[key], privateState, seen)}`,
  ).join(",")}}`
}

function serializeDescriptor(
  descriptor: PropertyDescriptor,
  privateState: ObservationScopePrivateState,
  seen: WeakSet<object>,
): string {
  if (!("value" in descriptor)) return "accessor"
  return stableSerialize(descriptor.value, privateState, seen)
}

function isProxySafe(value: unknown): value is Record<string, unknown> | unknown[] {
  if (value === null || typeof value !== "object") return false
  if (Array.isArray(value)) return true
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function randomObservationSalt(): string {
  const bytes = new Uint8Array(32)
  const crypto = globalThis.crypto
  if (crypto?.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
}

export function sha256ActionConsumerText(message: string): string {
  return bytesToHex(sha256ActionConsumerBytes(new TextEncoder().encode(message)))
}

function hmacSha256ActionConsumerText(key: Uint8Array, message: string): string {
  const blockSize = 64
  const normalizedKey = key.length > blockSize ? sha256ActionConsumerBytes(key) : Uint8Array.from(key)
  const paddedKey = new Uint8Array(blockSize)
  paddedKey.set(normalizedKey)
  const innerPad = paddedKey.map((byte) => byte ^ 0x36)
  const outerPad = paddedKey.map((byte) => byte ^ 0x5c)
  const messageBytes = new TextEncoder().encode(message)
  const inner = sha256ActionConsumerBytes(concatBytes(innerPad, messageBytes))
  return bytesToHex(sha256ActionConsumerBytes(concatBytes(outerPad, inner)))
}

function concatBytes(left: Uint8Array, right: Uint8Array): Uint8Array {
  const result = new Uint8Array(left.length + right.length)
  result.set(left)
  result.set(right, left.length)
  return result
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
}

function sha256ActionConsumerBytes(bytes: Uint8Array): Uint8Array {
  const bitLength = bytes.length * 8
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64
  const padded = new Uint8Array(paddedLength)
  padded.set(bytes)
  padded[bytes.length] = 0x80
  const view = new DataView(padded.buffer)
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000))
  view.setUint32(paddedLength - 4, bitLength >>> 0)

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])
  const k = SHA256_CONSTANTS
  const w = new Uint32Array(64)
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4)
    for (let i = 16; i < 64; i += 1) {
      const a = w[i - 15]
      const b = w[i - 2]
      const s0 = rotateRight(a, 7) ^ rotateRight(a, 18) ^ (a >>> 3)
      const s1 = rotateRight(b, 17) ^ rotateRight(b, 19) ^ (b >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0
    }
    let [a, b, c, d, e, f, g, hh] = h
    for (let i = 0; i < 64; i += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)
      const choose = (e & f) ^ (~e & g)
      const t1 = (hh + s1 + choose + k[i] + w[i]) >>> 0
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)
      const majority = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (s0 + majority) >>> 0
      hh = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }
    h[0] = (h[0] + a) >>> 0
    h[1] = (h[1] + b) >>> 0
    h[2] = (h[2] + c) >>> 0
    h[3] = (h[3] + d) >>> 0
    h[4] = (h[4] + e) >>> 0
    h[5] = (h[5] + f) >>> 0
    h[6] = (h[6] + g) >>> 0
    h[7] = (h[7] + hh) >>> 0
  }
  const digest = new Uint8Array(32)
  const digestView = new DataView(digest.buffer)
  for (let index = 0; index < h.length; index += 1) digestView.setUint32(index * 4, h[index]!)
  return digest
}

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits))
}

const SHA256_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])
